import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Select from '~components/Form/components/Select';
import InputField from '~components/Form/components/InputField';
import NavTabs from '~components/NavTabs';
import ModalAction from '~components/Modal/ModalAction';
import { useConfigureFooter } from '~hooks/useHelpers';
import useFunilObraUnidadeMedida from '~fetchs/Funnel/useFunilObraUnidadeMedida';
import useFunilObraGrupoProduto from '~fetchs/useFunilObraGrupoProduto';
import { db } from '~db/db';
import { initialPath } from '~misc/paths';
import { fixObject, formatDate, formatMonthYear, getDevice, isValidMonthYear } from '../helpers/funnelHelpers';
import { formatDateToCustomString } from '~pages/PriceSearchV2/utils/searchHelpers';
import useFunilStatus from '~fetchs/useFunilStatus';
import { randomId } from '~pages/Funnel/utils';
import useModalAction from '~components/Modal/hooks/useModalAction';
import originControl from '~services/utils/originControl';
import useRequisicoesOffline from '~fetchs/useRequisicoesOffline';
import MessageForm from '~components/Form/components/MessageForm';
import { IconSuccess, WarningIcon2 } from '~misc/icons';
import LoaderFull from '~components/Form/components/LoaderFull';
import { deviceType, isDesktop, isTablet } from 'react-device-detect';
import { getLocation } from '~services/utils/geoLocation';

const today = new Date();
today.setHours(0, 0, 0, 0);

const baseSchemaShape = {
  funilObraGrupoProdutoId: yup
    .number()
    .typeError('Grupo Produto é obrigatório')
    .required('Grupo Produto é obrigatório'),
  funilObraUnidadeMedidaId: yup
    .number()
    .typeError('Unidade de Medida é obrigatória')
    .required('Unidade de Medida é obrigatória'),
  volumeMapeado: yup
    .number()
    .typeError('Volume Mapeado é obrigatório')
    .required('Volume Mapeado é obrigatório'),
  volumeNegociado: yup
    .number()
    .typeError('Volume negociado é obrigatório')
    .required('Volume negociado é obrigatório'),
  dataNegociacao: yup
    .string()
    .required("Selecione o mês/ano")
    .transform((value) => value.split('-')?.reverse()?.join('/'))
    .test("valid-month-year", "Mês/ano inválido", value => {
      return isValidMonthYear(value);
    })
    .test("min-month-year", "O mês deve ser o atual ou futuro", value => {
      if (!value || !isValidMonthYear(value)) return false;

      const [month, year] = value.split("/").map(Number);

      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();

      return (
        year > currentYear ||
        (year === currentYear && month >= currentMonth)
      );
    })
};

const modalKind = {
  save: 'save',
};

const normalizeText = (value) =>
  (value ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();


const unit_group = [
  { groupKeywords: ['concreto'], unitKeywords: ['metro cubico', 'metros cubicos', 'm3', 'm³'] },
  { groupKeywords: ['agregado'], unitKeywords: ['metro cubico', 'metros cubicos', 'm3', 'm³'] },
  { groupKeywords: ['cimento'], unitKeywords: ['tonelada', 'ton'] },
  { groupKeywords: ['basica', 'ensacado', 'granel'], unitKeywords: ['tonelada', 'ton'] },
  { groupKeywords: ['colante'], unitKeywords: ['tonelada', 'ton'] },
];

const findDefaultUnitOption = (groupName, options) => {
  if (!groupName || !options?.length) return null;
  const normalizedGroup = normalizeText(groupName);
  const rule = unit_group.find((r) =>
    r.groupKeywords.some((kw) => normalizedGroup.includes(kw))
  );
  if (!rule) return null;
  for (const kw of rule.unitKeywords) {
    const match = options.find((o) => normalizeText(o.label).includes(kw));
    if (match) return match;
  }
  return null;
};

const buildAcompanhamentoInitialValues = (group) => ({
  funilObraGrupoProdutoId: '',
  funilObraUnidadeMedidaId: '',
  volumeMapeado: '',
  volumeNegociado: '',
  dataNegociacao: ''
});

const FunnelNewMappingGroup = () => {
  const formikRef = useRef();
  const navigate = useNavigate();
  const { parameter: clienteId } = useParams();
  const { origens } = originControl();
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  params.set("origem", origens.acompanhamento_mapeamento);

  const { save } = useRequisicoesOffline();  
  
  const [activeModal, setActiveModal] = useState(null);
  const [bulkDashboardItems, setBulkDashboardItems] = useState([]);

  const { funilObraGrupoProduto, optionFunilObraGruposProduto, funilObraGrupoProdutoSelect, onChangeFunilObraGrupoProduto, funilObraGrupoProdutoObjeto } = useFunilObraGrupoProduto({ formikRef });
  const { optionFunilObraUnidadeMedida, funilObraUnidadeMedidaSelect, onChangeFunilObraUnidadeMedida } = useFunilObraUnidadeMedida({ formikRef, funilObraGrupoProdutoId: funilObraGrupoProduto, showAllOptions: false });
  const { funilStatusObjBySlug } = useFunilStatus({});

  const [loading, setLoading] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState({
    text: "",
    type: "success",
    icon: IconSuccess,
  });

  const initialValues = buildAcompanhamentoInitialValues({});
  const validationSchema = yup.object(baseSchemaShape);

  const lastAutoSelectedGroupRef = useRef(null);

  const grupoNomeSelecionado = funilObraGrupoProdutoObjeto?.[`id-${funilObraGrupoProduto}`]?.nome;
  const defaultUnitOption = useMemo(
    () => findDefaultUnitOption(grupoNomeSelecionado, optionFunilObraUnidadeMedida),
    [grupoNomeSelecionado, optionFunilObraUnidadeMedida]
  );
  const isUnidadeMedidaLocked = Boolean(funilObraGrupoProduto && defaultUnitOption);

  useEffect(() => {
    if (!funilObraGrupoProduto || !optionFunilObraUnidadeMedida?.length) return;
    if (lastAutoSelectedGroupRef.current === funilObraGrupoProduto) return;

    const target = defaultUnitOption ?? (optionFunilObraUnidadeMedida.length === 1 ? optionFunilObraUnidadeMedida[0] : null);

    if (target) {
      onChangeFunilObraUnidadeMedida(target);
    }
    lastAutoSelectedGroupRef.current = funilObraGrupoProduto;
  }, [funilObraGrupoProduto, optionFunilObraUnidadeMedida, defaultUnitOption]);

  const {
      isModalOpenAction: isModalRepeatedOpenAction,
      closeModalAction: closeModalRepeatedAction,
      openModalAction: openModalRepeatedAction,
    } = useModalAction();

  const tabs = [
    {
      name: 'Funil de Obras',
      link: globalThis.location.pathname,
    },
  ];

  const closeModal = () => setActiveModal(null);

  const handleDataNegociacao = (val) => {
    // const date = formatMonthYear(val);
    formikRef?.current?.setFieldValue('dataNegociacao', val);
  }

  const handleSubmit = () => {
    setActiveModal(modalKind.save);
  };

  const repeated = async (data) => {
    const results = [];
    await db.funilObraDashboard
      .where('clienteId')
      .equals(parseInt(clienteId))
      .and(
        (c) =>
          // c.ativo &&
          c.funilObraGrupoProdutoId == data.funilObraGrupoProdutoId &&
          !c.deletedAt
      )
      .each((c) => results.push(c));

    return results?.length > 0 ? results[results?.length - 1] : null;
  };

  const confirmBulkDashboardChanges = async () => {
    closeModalRepeatedAction();

    setLoading(true);

    await handleSubmitFunnel(bulkDashboardItems);
  }

  const handleSubmitFunnel = async (confirmedData) => {
    closeModal();

    const dispositivo = getDevice(isTablet, isDesktop, deviceType);
    let pos = await getLocation();
    for (const e of Object.keys(pos)) 
      pos[e] = pos[e] ? pos[e].toString() : null;

    const toSend = {
      ...confirmedData,
      dispositivo,
      ...pos,
      dataAtualizacao: formatDateToCustomString(new Date),
      createdAt: formatDateToCustomString(new Date),
    };

    const obj = {
      id: randomId(15),
      type: "funilObraNovoGrupo",
      clienteId,
      toSend,
      createdAt: new Date().toISOString(),
      sended: false,
    };

    try {
      const response = await save(obj);

      if (response?.isOffline) {
        const exist = await repeated(toSend);
        if(exist?.id)
          await db.funilObraDashboard.delete(exist.id);

        setShowMessageForm({
          text: `Novo grupo inserido com sucesso (modo offline)`,
          icon: IconSuccess,
          type: "success",
        });

        if (params.get("from") === "direto" && params.get("retorno") === "roteirizacao") {
          navigate(
            `${initialPath}/funil-de-obras/${clienteId}/acompanhamento-mapeamento?${params.toString()}`,
            { replace: true, state: { skipRedirect: true, groupSaved: true } }
          );
        }
        
        return;
      }

      if(response && Array.isArray(response)) {
        const data = response;
        const first = data[0] ?? null;

        if(first) {
          if(first?.funilObraGrupoProdutoId) {
            await db.transaction('rw', db.funilObra, db.funilObraDashboard, async () => {
                await db.funilObra
                  .where('clienteId')
                  .equals(parseInt(clienteId)).and(x => x.funilObraGrupoProdutoId === first.funilObraGrupoProdutoId).delete();
    
                await db.funilObraDashboard
                  .where('clienteId')
                  .equals(parseInt(clienteId)).and(x => x.funilObraGrupoProdutoId === first.funilObraGrupoProdutoId).delete();
            });
          }
        }

        await db.funilObra.bulkAdd(data);
        await db.funilObraDashboard.bulkAdd(data.map(e => fixObject({ obj: e })));
      } 

      navigate(
        `${initialPath}/funil-de-obras/${clienteId}/acompanhamento-mapeamento?${params.toString()}`,
        {
          replace: true,
          state: { skipRedirect: true, groupSaved: true },
        }
      );
      
      // navigate(`${initialPath}/funil-de-obras/${clienteId}/acompanhamento-mapeamento?origem=${origens.acompanhamento_mapeamento}`,{ state: { skipRedirect: true }});
    } catch (error) {
      const message = error?.message ?? "";
      setShowMessageForm({
        text: `Ocorreu um erro durante o envio do grupo - ${message ?? ""}`,
        icon: WarningIcon2,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  const confirmarSalvamento = async () => {
    const values = formikRef.current?.values;
    const tosave = {
      ...values,
      dataNegociacao: `${values.dataNegociacao}-01`
    }

    const exist = await repeated(tosave);
    const toAdd = {
      ...tosave,
      dataNegociacao: tosave.dataNegociacao,
      clienteId: parseInt(clienteId),
      funilStatusId: funilStatusObjBySlug['em-negociacao']?.id ?? 0,
      createdAt: formatDateToCustomString(new Date()),
      ativo: true,
      dispositivo: getDevice(isTablet, isDesktop, deviceType),
      id: 0
    };

    if(exist) {
      closeModal();
      openModalRepeatedAction();
      setBulkDashboardItems(
        toAdd
      );
      return;
    }

    setLoading(true);

    await handleSubmitFunnel(toAdd);
  };

  useConfigureFooter({ form: true, formId: 'novo', submitLabel: 'Mapear', disabled: false });

  return (
    <section className="container">
      {
        loading ? <LoaderFull /> : null
      }
      <NavTabs navs={tabs} customClasses={'!flex-none !w-[40%]'} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        innerRef={formikRef}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, values }) => {
          return (
            <Form id="novo">
              <div className="relative containerForm mt-4 rounded-xl bg-white p-7">
                  <div className="sm:flex justify-between py-3">
                    <h2 className="text-2xl font-bold title pb-6">
                      Dados Gerais
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      options={optionFunilObraGruposProduto}
                      label="Grupo Produto:"
                      name="funilObraGrupoProdutoId"
                      placeholder="Grupo Produto"
                      isRequired
                      onChange={(e) => onChangeFunilObraGrupoProduto(e)}
                      spacingControl={true}
                      errors={errors.funilObraGrupoProdutoId}
                      touched={touched.funilObraGrupoProdutoId}
                      {...funilObraGrupoProdutoSelect}
                    />

                    <InputField
                      labelName="Data de Negociação:"
                      type="month"
                      inputName="dataNegociacao"
                      inputMask="offmask"
                      isRequired
                      value={values.dataNegociacao}
                      onChange={(e) => handleDataNegociacao(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      options={isUnidadeMedidaLocked ? [defaultUnitOption] : optionFunilObraUnidadeMedida}
                      label="Unidade de Medida:"
                      name="funilObraUnidadeMedidaId"
                      placeholder="Unidade de Medida"
                      isRequired
                      isDisabled={isUnidadeMedidaLocked}
                      onChange={(e) => onChangeFunilObraUnidadeMedida(e)}
                      spacingControl={true}
                      errors={errors.funilObraUnidadeMedidaId}
                      touched={touched.funilObraUnidadeMedidaId}
                      {...funilObraUnidadeMedidaSelect}
                    />

                    <div className="flex gap-x-2">
                      <InputField
                        labelName="Volume Mapeado:"
                        tooltip="Volume total estimado para obra"
                        inputName="volumeMapeado"
                        inputMask="offmask"
                        inputType="number"
                        isRequired
                        placeholder="Volume Mapeado"
                        onWheel={(e) => e.target.blur()}
                      />

                      <InputField
                        labelName="Volume Negociado:"
                        tooltip="Volume em negociação"
                        inputName="volumeNegociado"
                        inputMask="offmask"
                        inputType="number"
                        isRequired
                        placeholder="Volume Negociado"
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>
                  </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      {showMessageForm.text ? (
        <MessageForm
          text={showMessageForm.text}
          type={showMessageForm.type}
          showMessage={showMessageForm}
          setShowMessage={() =>
            setShowMessageForm({
              text: "",
            })
          }
          icon={showMessageForm.icon}
        />
      ) : null}

      <ModalAction
        isOpen={isModalRepeatedOpenAction}
        onClose={closeModalRepeatedAction}
        title="Esse item já contém uma pesquisa vigente!"
        sub="Deseja seguir para a substituição dos dados?"
        buttonOne="Sim"
        buttonTwo="Não"
        linkOne={() => confirmBulkDashboardChanges()}
        linkTwo={() => {
          setBulkDashboardItems([]);
          closeModalRepeatedAction();
        }}
      />

      <ModalAction
        isOpen={activeModal === modalKind.save}
        onClose={closeModal}
        title="Confirmar salvamento"
        sub="Deseja confirmar o salvamento dos dados?"
        status="success"
        buttonOne="Confirmar"
        buttonTwo="Cancelar"
        linkOne={confirmarSalvamento}
        linkTwo={closeModal}
      />
    </section>
  );
};

export default FunnelNewMappingGroup;
